import type { PendingRecordingSnapshot, RecordingSnapshotStore } from './types';

const DATABASE_NAME = 'vd-recorder-snapshots-v1';
const DATABASE_VERSION = 1;
const STORE_NAME = 'snapshots';
let openPromise: Promise<IDBDatabase> | null = null;

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Snapshot storage request failed.'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('Snapshot transaction aborted.'));
    transaction.onerror = () => reject(transaction.error ?? new Error('Snapshot transaction failed.'));
  });
}

function isBlobLike(value: unknown, type: string): value is Blob {
  if (!value || typeof value !== 'object') return false;
  const blob = value as Partial<Blob>;
  return blob.type === type
    && typeof blob.size === 'number'
    && blob.size >= 0
    && typeof blob.slice === 'function';
}

function validSnapshot(value: unknown): value is PendingRecordingSnapshot {
  if (!value || typeof value !== 'object') return false;
  const row = value as Partial<PendingRecordingSnapshot>;
  return typeof row.key === 'string' && row.key.length > 0
    && typeof row.ownerId === 'string' && row.ownerId.length > 0
    && typeof row.recordingId === 'string' && row.recordingId.length > 0
    && typeof row.segmentId === 'string' && row.segmentId.length > 0
    && typeof row.projectId === 'string' && row.projectId.length > 0
    && typeof row.requestId === 'string' && row.requestId.length > 0
    && Number.isSafeInteger(row.capturedAtMs) && (row.capturedAtMs ?? -1) >= 0
    && (row.trigger === 'start' || row.trigger === 'finish' || row.trigger === 'manual')
    && typeof row.sourceUrl === 'string'
    && isBlobLike(row.screenshot, 'image/png')
    && isBlobLike(row.thumbnail, 'image/webp')
    && (row.state === 'pending' || row.state === 'uploading' || row.state === 'failed')
    && Number.isSafeInteger(row.attempts) && (row.attempts ?? -1) >= 0
    && typeof row.createdAt === 'string' && Number.isFinite(Date.parse(row.createdAt))
    && typeof row.updatedAt === 'string' && Number.isFinite(Date.parse(row.updatedAt))
    && typeof row.expiresAt === 'string' && Number.isFinite(Date.parse(row.expiresAt));
}

function openDatabase(): Promise<IDBDatabase> {
  if (openPromise) return openPromise;
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('IndexedDB is unavailable.'));
  let promise!: Promise<IDBDatabase>;
  let abandoned = false;
  promise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (database.objectStoreNames.contains(STORE_NAME)) return;
      const store = database.createObjectStore(STORE_NAME, { keyPath: 'key' });
      store.createIndex('ownerId', 'ownerId', { unique: false });
      store.createIndex('recordingId', 'recordingId', { unique: false });
      store.createIndex('expiresAt', 'expiresAt', { unique: false });
    };
    request.onsuccess = () => {
      const database = request.result;
      if (abandoned) {
        database.close();
        return;
      }
      database.onversionchange = () => {
        database.close();
        if (openPromise === promise) openPromise = null;
      };
      resolve(database);
    };
    request.onerror = () => {
      if (openPromise === promise) openPromise = null;
      reject(request.error ?? new Error('Unable to open snapshot storage.'));
    };
    request.onblocked = () => {
      abandoned = true;
      if (openPromise === promise) openPromise = null;
      reject(new Error('Snapshot storage upgrade is blocked by another tab.'));
    };
  });
  openPromise = promise;
  return promise;
}

async function listByIndex(
  indexName: 'ownerId' | 'recordingId',
  value: string,
): Promise<PendingRecordingSnapshot[]> {
  const database = await openDatabase();
  const transaction = database.transaction(STORE_NAME, 'readonly');
  const done = transactionDone(transaction);
  const rows = await requestValue(
    transaction.objectStore(STORE_NAME).index(indexName).getAll(IDBKeyRange.only(value)),
  ).catch(async (error) => {
    await done.catch(() => {});
    throw error;
  });
  await done;
  return (rows as unknown[]).filter(validSnapshot).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

async function deleteByIndex(indexName: 'ownerId' | 'recordingId' | 'expiresAt', range: IDBKeyRange) {
  const database = await openDatabase();
  const transaction = database.transaction(STORE_NAME, 'readwrite');
  const done = transactionDone(transaction);
  const store = transaction.objectStore(STORE_NAME);
  const cursor = store.index(indexName).openKeyCursor(range);
  cursor.onsuccess = () => {
    const current = cursor.result;
    if (!current) return;
    store.delete(current.primaryKey);
    current.continue();
  };
  await done;
}

export class IndexedDbRecordingSnapshotStore implements RecordingSnapshotStore {
  async putSnapshot(snapshot: PendingRecordingSnapshot): Promise<void> {
    if (!validSnapshot(snapshot)) throw new Error('Invalid pending recording snapshot.');
    const database = await openDatabase();
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const done = transactionDone(transaction);
    transaction.objectStore(STORE_NAME).put(snapshot);
    await done;
  }

  async getSnapshot(key: string): Promise<PendingRecordingSnapshot | null> {
    const database = await openDatabase();
    const transaction = database.transaction(STORE_NAME, 'readonly');
    const done = transactionDone(transaction);
    const value = await requestValue(transaction.objectStore(STORE_NAME).get(key)).catch(async (error) => {
      await done.catch(() => {});
      throw error;
    });
    await done;
    return validSnapshot(value) ? value : null;
  }

  async deleteSnapshot(key: string): Promise<void> {
    const database = await openDatabase();
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const done = transactionDone(transaction);
    transaction.objectStore(STORE_NAME).delete(key);
    await done;
  }

  listSnapshotsForOwner(ownerId: string): Promise<PendingRecordingSnapshot[]> {
    return listByIndex('ownerId', ownerId);
  }

  listSnapshotsForRecording(recordingId: string): Promise<PendingRecordingSnapshot[]> {
    return listByIndex('recordingId', recordingId);
  }

  deleteSnapshotsForOwner(ownerId: string): Promise<void> {
    return deleteByIndex('ownerId', IDBKeyRange.only(ownerId));
  }

  deleteSnapshotsForRecording(recordingId: string): Promise<void> {
    return deleteByIndex('recordingId', IDBKeyRange.only(recordingId));
  }

  pruneExpiredSnapshots(now: Date): Promise<void> {
    return deleteByIndex('expiresAt', IDBKeyRange.upperBound(now.toISOString()));
  }
}

export async function resetSnapshotDatabaseForTests(): Promise<void> {
  const database = openPromise ? await openPromise.catch(() => null) : null;
  database?.close();
  openPromise = null;
  if (typeof indexedDB === 'undefined') return;
  await requestValue(indexedDB.deleteDatabase(DATABASE_NAME));
}
